import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AI_PROVIDER } from './constants/ai';
import { fetchActiveStocks } from './fetchActiveStocks';
import { filterStocks } from './filterStocks';
import { fetchLastTrade, fetchPreviousClose, fetchMarketStatus, fetchMarketContext, buildAiPayload, sendClaudeAnalysis, sendOpenaiAnalysis, buildRiskPayload, fetchBatchSnapshots } from './apiHelpers';
import { streamRiskAssessment } from './services/riskStreamService';
import { StockTable } from './StockTable';
import { Pagination } from './Pagination';
import { calculateAvgChangePercent } from './utils/priceCalculations';
import { getProfile as apiGetProfile, saveScan as apiSaveScan } from './services/apiService';
import { formatMarketSentiment, getSentimentEmoji, getSentimentColor, getSpreadEmoji, getSpreadColor, ET_TIMEZONE } from './utils/marketUtils';
import { getErrorLabel } from './utils/messages';
import StockWebSocket from './websocketService';
import { formatInTimeZone } from 'date-fns-tz';
import AiAnalysisPanel from './AiAnalysisPanel';
import BrokerDataImport from './BrokerDataImport';
import TradingJournal from './TradingJournal';
import InsightsDashboard from './InsightsDashboard';
import AiReviewDialog from './AiReviewDialog';
import ProfileUpdateDialog from './ProfileUpdateDialog';
import BacktestDialog from './BacktestDialog';

// Get market status color and emoji
const getMarketStatusDisplay = (status) => {
  switch (status) {
    case 'open': return { color: '#22c55e', emoji: '🟢', text: 'open' };
    case 'closed': return { color: '#ef4444', emoji: '🔴', text: 'closed' };
    case 'extended-hours': return { color: '#eab308', emoji: '🟡', text: 'extended' };
    default: return { color: '#6b7280', emoji: '⚪', text: status || 'unknown' };
  }
};

// Get data source display
const getDataSourceDisplay = (useWebSocket, wsConnected, restFallbackActive) => {
  if (useWebSocket) {
    if (wsConnected) {
      return { emoji: '🟢', text: 'WebSocket' };
    } else if (restFallbackActive) {
      return { emoji: '🟡', text: 'REST' };
    } else {
      return { emoji: '🔴', text: 'WebSocket' };
    }
  } else {
    return { emoji: '🔵', text: 'REST' };
  }
};

// =====================================================================
// Status chips for the top health row (introduced 2026-06-04 after 6/3
// MarketContext outage). Renders 4 chips: Market / Data / Backend / Polygon.
// Each chip is "[emoji] Subject state" with color-coded states.
// =====================================================================
const CHIP_COLOR_OK    = '#16a34a';
const CHIP_COLOR_WARN  = '#f59e0b';
const CHIP_COLOR_ALERT = '#ea580c';
const CHIP_COLOR_ERROR = '#dc2626';
const CHIP_COLOR_INFO  = '#2563eb';
const CHIP_COLOR_DIM   = '#d1d5db';

const buildMarketChip = (marketStatus) => {
  if (!marketStatus) return { emoji: '⚪', text: 'Market unknown', color: CHIP_COLOR_DIM };
  switch (marketStatus.market) {
    case 'open':           return { emoji: '🟢', text: 'Market open',     color: CHIP_COLOR_OK };
    case 'extended-hours': return { emoji: '🟡', text: 'Market extended', color: CHIP_COLOR_WARN };
    case 'closed':         return { emoji: '🔴', text: 'Market closed',   color: CHIP_COLOR_ERROR };
    default:               return { emoji: '⚪', text: 'Market unknown',  color: CHIP_COLOR_DIM };
  }
};

// Data chip reflects the FRONTEND's data source for stock prices (WebSocket
// vs REST to Polygon). That connection is direct browser → polygon.io and
// has nothing to do with stock-ai-service's reachability — so do NOT gate
// this chip on backendReachable. Bug seen 2026-06-06 with EC2 stopped:
// indices were still streaming from Polygon but Data chip showed "—".
const buildDataChip = (useWebSocket, wsConnected, restFallbackActive) => {
  if (!useWebSocket)     return { emoji: '🔵', text: 'Data REST',       color: CHIP_COLOR_INFO };
  if (wsConnected)       return { emoji: '🟢', text: 'Data WebSocket',  color: CHIP_COLOR_OK };
  if (restFallbackActive)return { emoji: '🟡', text: 'Data REST',       color: CHIP_COLOR_WARN };
  return                       { emoji: '🔴', text: 'Data offline',    color: CHIP_COLOR_ERROR };
};

const buildBackendChip = (backendReachable) => {
  return backendReachable
    ? { emoji: '🟢', text: 'Backend online',  color: CHIP_COLOR_OK }
    : { emoji: '🔴', text: 'Backend offline', color: CHIP_COLOR_ERROR };
};

// Polygon chip reflects whether the Polygon SERVICE is reachable. Combines
// backend's view (its /api/health → marketContextUpstream, which exercises
// the backend → Polygon REST path) with the frontend's view (any active
// browser → Polygon connection via WebSocket or REST). Backend is preferred
// when reachable since it has richer signal (success counters, cache age).
// Frontend fills in when backend is unreachable so EC2 weekend downtime
// doesn't falsely dim the chip.
//
// Three states (no more "⚪ —" ambiguity):
//   🟢 online    — any path working
//   🟡 cached    — backend explicitly serving from cache
//   🔴 offline   — confirmed unreachable from at least one observed path
//
// Aligned with Data chip: Data 🔴 offline ⟺ Polygon 🔴 offline,
// Data 🟢/🟡/🔵 ⟹ Polygon 🟢 online.
const buildPolygonChip = (backendHealth, backendReachable,
                          wsConnected, restFallbackActive, useWebSocket) => {
  const mc = backendReachable ? backendHealth?.checks?.marketContextUpstream : null;

  // Backend has an authoritative answer → trust it
  if (mc?.status === 'up')       return { emoji: '🟢', text: 'Polygon online', color: CHIP_COLOR_OK };
  if (mc?.status === 'degraded') {
    const min = Math.max(1, Math.round((mc.details?.cacheAgeSeconds || 60) / 60));
    return { emoji: '🟡', text: `Polygon online (cached ${min}m)`, color: CHIP_COLOR_WARN };
  }
  if (mc?.status === 'down')     return { emoji: '🔴', text: 'Polygon offline', color: CHIP_COLOR_ERROR };

  // No backend signal (offline, not yet loaded, or status unknown) → fall back
  // to frontend evidence. Any active mode (live WS, auto REST fallback, or
  // user-selected REST) means the browser is talking to Polygon directly.
  const frontendOnline = wsConnected || restFallbackActive || !useWebSocket;
  return frontendOnline
    ? { emoji: '🟢', text: 'Polygon online',  color: CHIP_COLOR_OK }
    : { emoji: '🔴', text: 'Polygon offline', color: CHIP_COLOR_ERROR };
};

const StatusChip = ({ emoji, text, color }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    color,
    fontWeight: color === CHIP_COLOR_OK || color === CHIP_COLOR_DIM ? 'normal' : 'bold',
  }}>
    <span>{emoji}</span>
    <span>{text}</span>
  </span>
);

const MAX_DATA_POINTS = 180; // 90 minutes * 2 data points per minute (30-second intervals)
const UPDATE_INTERVAL = 30000; // 30 seconds
const STOCKS_PER_PAGE = 30;
const REST_POLL_LIMIT = 60; // When market closed, only poll top 60 stocks by Daily Gain

// AI Analysis stock count options (user configurable)
const AI_STOCK_COUNT_OPTIONS = [100, 120, 150, 200];
const DEFAULT_AI_STOCK_COUNT = 100;

// Dynamic config options
const PRICE_CONFIGS = [
  { key: '24/8', total: 24, recent: 8, label: 'Early', description: 'Early (9:30-10:30)', startHour: 9, startMin: 30, endHour: 10, endMin: 30 },
  { key: '30/10', total: 30, recent: 10, label: 'Mid-day', description: 'Mid-day (10:30-14:00)', startHour: 10, startMin: 30, endHour: 14, endMin: 0 },
  { key: '60/20', total: 60, recent: 20, label: 'Late', description: 'Late (14:00-16:00)', startHour: 14, startMin: 0, endHour: 16, endMin: 0 }
];

// Default config from env or fallback to 24/8
const getDefaultConfigKey = () => {
  const envTotal = parseInt(process.env.REACT_APP_TOTAL_PRICE_COUNT);
  const envRecent = parseInt(process.env.REACT_APP_RECENT_PRICE_COUNT);
  if (envTotal === 30 && envRecent === 10) return '30/10';
  if (envTotal === 60 && envRecent === 20) return '60/20';
  return '24/8';
};

const DEFAULT_CONFIG_KEY = getDefaultConfigKey();
const DEFAULT_CONFIG = PRICE_CONFIGS.find(c => c.key === DEFAULT_CONFIG_KEY) || PRICE_CONFIGS[0];

// Batch processing configuration for initial data fetch
const INIT_BATCH_SIZE = 50; // Number of stocks to fetch per batch (50×2=100 req/s, exactly at limit)
const INIT_BATCH_DELAY = 0; // No delay - maximum speed

// Acceleration configuration based on selected config
const getAccelerationConfig = (configKey) => {
  switch(configKey) {
    case '24/8': return { interval: 4, threshold: 0.05 };   // 2 minutes
    case '30/10': return { interval: 6, threshold: 0.08 };   // 3 minutes
    case '60/20': return { interval: 10, threshold: 0.10 };  // 5 minutes
    default: return { interval: 6, threshold: 0.08 };
  }
};

// Calculate acceleration arrow based on avgChangePercent history
const getAccelerationArrowWithConfig = (history, accelConfig) => {
  if (!history || history.length < accelConfig.interval) {
    return ''; // Not enough data
  }

  const oldest = history[0];
  const newest = history[history.length - 1];
  const change = newest - oldest;

  if (Math.abs(change) < accelConfig.threshold) {
    return '→'; // Steady
  } else if (change > 0) {
    return '↗'; // Accelerating
  } else {
    return '↘'; // Decelerating
  }
};

// Calculate stock score based on daily gain, avg change, and acceleration
const calculateStockScore = (dailyGain, avgChange, accelerationArrow, relativeVolume = null) => {
  let points = 0;

  // Daily Gain points (0-3)
  if (dailyGain >= 10) points += 3;
  else if (dailyGain >= 5) points += 2;
  else if (dailyGain >= 0) points += 1;
  // < 0: 0 points

  // Avg Change points (0-3)
  if (avgChange >= 2) points += 3;
  else if (avgChange >= 1) points += 2;
  else if (avgChange >= 0) points += 1;
  // < 0: 0 points

  // Acceleration points (0-2)
  if (accelerationArrow === '↗') points += 2;
  else if (accelerationArrow === '→') points += 1;
  // ↘: 0 points

  // Relative Volume points (0-2)
  if (relativeVolume != null) {
    if (relativeVolume >= 3.0) points += 2;
    else if (relativeVolume >= 1.5) points += 1;
  }

  // Convert points to grade (adjusted for max 10 points)
  if (points >= 9) return { grade: 'A+', emoji: '🟢🟢', color: '#15803d', points };
  if (points >= 7) return { grade: 'A', emoji: '🟢', color: '#22c55e', points };
  if (points >= 5) return { grade: 'B', emoji: '🟡', color: '#eab308', points };
  if (points >= 3) return { grade: 'C', emoji: '🟠', color: '#f97316', points };
  return { grade: 'D', emoji: '🔴', color: '#ef4444', points };
};

// Phase 2: Learning Loop - Calculate personalized score adjustment
// Parameters:
//   - userProfile: user's trading profile from latest-insights.json
//   - marketSentiment: current market sentiment (bullish/mixed/bearish)
//   - aiResult: optional AI analysis result for this stock (from anthropicResult or openaiResult)
const calculatePersonalizedAdjustment = (userProfile, marketSentiment, aiResult = null) => {
  if (!userProfile) {
    return {
      adjustment: 0,
      reasons: [],
      hasPositiveMatch: false,
      hasNegativeMatch: false,
      matchedPattern: null,
      isSuccessPattern: false,
      isAvoidPattern: false
    };
  }

  let adjustment = 0;
  const reasons = [];
  let matchedPattern = null;
  let isSuccessPattern = false;
  let isAvoidPattern = false;

  // Rule 1: Market sentiment matching (+1)
  const bestSentiment = userProfile.bestMarketConditions?.sentiment?.toLowerCase();
  if (bestSentiment && marketSentiment?.toLowerCase() === bestSentiment) {
    adjustment += 1;
    reasons.push({ type: 'positive', text: `市场情绪: ${marketSentiment} (+1)` });
  }

  // Rule 2: AI Pattern matching (only if aiResult is provided)
  if (aiResult) {
    // Success pattern matching (+1 or +2 based on win rate)
    if (aiResult.matchesUserSuccessPattern) {
      isSuccessPattern = true;
      matchedPattern = aiResult.userSuccessPatternName;

      // Find the pattern in userProfile to get win rate
      const pattern = userProfile.successfulPatterns?.find(
        p => p.pattern === aiResult.userSuccessPatternName
      );
      const winRate = pattern?.winRate || 70;
      const meta = pattern?._meta;

      let adj = winRate >= 80 ? 2 : winRate >= 60 ? 1 : 0;

      // Decay: reduce score for declining/new/stale patterns
      if (adj > 0 && meta) {
        if (meta.trend === 'declining' || meta.trend === 'new') adj = Math.max(0, adj - 1);
        if ((meta.missedReviews || 0) >= 3) adj = Math.max(0, adj - 1);
      }

      if (adj > 0) {
        adjustment += adj;
        const decayNote = meta?.trend === 'declining' ? ' declining' : meta?.trend === 'new' ? ' new' : '';
        reasons.push({ type: 'positive', text: `Pattern: ${matchedPattern} ${winRate}%胜率 (+${adj})${decayNote}` });
      }
    }

    // Avoid pattern matching (-1 or -2 based on win rate)
    if (aiResult.matchesUserAvoidPattern) {
      isAvoidPattern = true;
      matchedPattern = aiResult.userAvoidPatternName;

      // Find the pattern in userProfile to get win rate
      const pattern = userProfile.avoidPatterns?.find(
        p => p.pattern === aiResult.userAvoidPatternName
      );
      const winRate = pattern?.winRate || 30;

      if (winRate < 20) {
        adjustment -= 2;
        reasons.push({ type: 'negative', text: `Pattern: ${matchedPattern} ${winRate}%胜率 (-2)` });
      } else {
        adjustment -= 1;
        reasons.push({ type: 'negative', text: `Pattern: ${matchedPattern} ${winRate}%胜率 (-1)` });
      }
    }
  }

  // Cap adjustment to +3/-2
  adjustment = Math.max(-2, Math.min(3, adjustment));

  return {
    adjustment,
    reasons,
    hasPositiveMatch: reasons.some(r => r.type === 'positive'),
    hasNegativeMatch: reasons.some(r => r.type === 'negative'),
    matchedPattern,
    isSuccessPattern,
    isAvoidPattern
  };
};

// Sort strategies for day trading
const SORT_STRATEGIES = {
  aggressive: {
    name: 'Aggressive',
    emoji: '🔥',
    criteria: [
      { field: 'dailyGainPercentage', order: 'desc' },
      { field: 'score', order: 'desc' }
    ],
    shorthand: 'DG→Sc',
    description: 'Catch breakouts, best for market open'
  },
  balanced: {
    name: 'Balanced',
    emoji: '⚖️',
    criteria: [
      { field: 'dailyGainPercentage', order: 'desc' },
      { field: 'score', order: 'desc' },
      { field: 'avgChangePercent', order: 'desc' }
    ],
    shorthand: 'DG→Sc→Avg',
    description: 'All-day trading'
  },
  steady: {
    name: 'Steady',
    emoji: '📈',
    criteria: [
      { field: 'avgChangePercent', order: 'desc' },
      { field: 'score', order: 'desc' }
    ],
    shorthand: 'Avg→Sc',
    description: 'Trend-first, midday/afternoon'
  },
  conservative: {
    name: 'Conservative',
    emoji: '🛡️',
    criteria: [
      { field: 'score', order: 'desc' }
    ],
    shorthand: 'Sc',
    description: 'Quality-first, when uncertain'
  }
};

const StockChartTable = ({ username, onLogout }) => {
  const [allStocks, setAllStocks] = useState([]);
  const [displayedStocks, setDisplayedStocks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [marketStatus, setMarketStatus] = useState(null);
  // Multi-column sorting (up to 3 columns)
  // Each item: { field: string, order: 'asc' | 'desc' }
  const [currentStrategy, setCurrentStrategy] = useState('balanced');
  const [sortCriteria, setSortCriteria] = useState(SORT_STRATEGIES.balanced.criteria);
  const [isStrategyMenuOpen, setIsStrategyMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [loadingProgress, setLoadingProgress] = useState({ phase: 1, phaseName: '', current: 0, total: 0 });
  const [error, setError] = useState(null);
  const initializationRef = useRef(false);
  const [useWebSocket, setUseWebSocket] = useState(true); // Toggle WebSocket mode
  const [wsConnected, setWsConnected] = useState(false); // WebSocket connection status
  const [restFallbackActive, setRestFallbackActive] = useState(false); // REST fallback when WebSocket fails
  const wsRef = useRef(null);
  const [isUIPaused, setIsUIPaused] = useState(false); // Pause UI updates while still collecting data
  const [currentNyTime, setCurrentNyTime] = useState(new Date()); // Local clock for NY time
  const strategyMenuRef = useRef(null); // Ref for strategy dropdown
  const configMenuRef = useRef(null); // Ref for config dropdown
  const hasLoggedRestMode = useRef(false); // Track if REST mode has been logged
  const [marketIndices, setMarketIndices] = useState([]); // Market index data (SPY, QQQ, DIA, IWM)
  const [marketSentiment, setMarketSentiment] = useState('loading'); // bullish, mixed, bearish, loading, error
  const [spreadClass, setSpreadClass] = useState(null); // severe-narrow, mild-narrow, balanced, small-cap-leading
  const [iwmSpyspread, setIwmSpyspread] = useState(null); // numeric IWM-SPY spread %
  const [trendScore, setTrendScore] = useState(null); // backend-computed weighted trend score
  const [marketErrorType, setMarketErrorType] = useState(null); // ERROR_TYPES key when fetch fails
  const [marketContextUpdatedAt, setMarketContextUpdatedAt] = useState(null); // ms-since-epoch of last successful market context fetch (drives "X min ago" stale tooltip when backend errors)
  const [userProfile, setUserProfile] = useState(null); // Phase 2: Learning Loop - user trading profile
  const [selectedConfig, setSelectedConfig] = useState(DEFAULT_CONFIG); // Dynamic price config
  const [isConfigMenuOpen, setIsConfigMenuOpen] = useState(false); // Config dropdown state
  const [avgChangeByConfig, setAvgChangeByConfig] = useState({}); // Store avgChange for all configs per symbol
  const [avgChangeHistoryByConfig, setAvgChangeHistoryByConfig] = useState({}); // Store history for all configs
  const [isRestLimitedMode, setIsRestLimitedMode] = useState(false); // Track if REST is limited to top stocks
  // Backend health snapshot (polled every 30s, drives the status chips row).
  // Shape: { db, anthropic, openai, lastCronRun, cronCompleteness, marketContextUpstream, ... }
  const [backendHealth, setBackendHealth] = useState(null);
  const [backendReachable, setBackendReachable] = useState(true);

  // AI Analysis Panel state
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const [aiAnthropicResult, setAiAnthropicResult] = useState(null);
  const [aiOpenaiResult, setAiOpenaiResult] = useState(null);
  const [isAnthropicLoading, setIsAnthropicLoading] = useState(false); // Progressive loading: Claude
  const [isOpenaiLoading, setIsOpenaiLoading] = useState(false); // Progressive loading: OpenAI
  const [aiScanTime, setAiScanTime] = useState(null); // Timestamp of last AI scan
  const [showAiPicksOnly, setShowAiPicksOnly] = useState(false); // Filter to show only AI recommended stocks
  const [aiStockCount, setAiStockCount] = useState(DEFAULT_AI_STOCK_COUNT); // User configurable stock count
  // Risk Assessment state
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [isRiskLoading, setIsRiskLoading] = useState(false);
  const [aiPayloadCache, setAiPayloadCache] = useState(null); // Cached enriched stock data for risk assessment
  const [isScanSaved, setIsScanSaved] = useState(false); // Track if current scan has been saved
  // AbortController for the streaming risk assessment. Held in a ref so
  // useEffect dep changes don't trigger cleanup that would abort our own
  // in-flight stream. See the streaming-risk useEffect below for details.
  const riskControllerRef = useRef(null);

  // AI Panel resizing state.
  //
  // Default width: 54% of viewport (cap 300-1400). On a typical 1536
  // CSS-px viewport (1920×1080 with 125% Windows scaling), this yields
  // ~829 px panel + ~707 px stock area + ~530 px chart — matches the
  // user's preferred 824 px sizing within a couple of pixels while
  // scaling proportionally on other displays.
  //
  // Migration: anything ≤800 px in localStorage (long-ago 600 or
  // narrower-viewport defaults) is overridden. >800 px is preserved
  // as deliberate user sizing.
  const defaultAiPanelWidth = () => {
    const target = Math.round((typeof window !== 'undefined' ? window.innerWidth : 1600) * 0.54);
    return Math.max(300, Math.min(target, 1400));
  };
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('aiPanelWidth') || '', 10);
    if (!Number.isFinite(saved) || saved <= 800) return defaultAiPanelWidth();
    return saved;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // AI Panel resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    const containerWidth = window.innerWidth;
    const newWidth = containerWidth - e.clientX;
    // Constrain width: min 300px, max 85% of screen (panel-first layout)
    const constrainedWidth = Math.max(300, Math.min(newWidth, containerWidth * 0.85));
    setAiPanelWidth(constrainedWidth);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      localStorage.setItem('aiPanelWidth', aiPanelWidth.toString());
    }
  }, [isResizing, aiPanelWidth]);

  const handleResizeDoubleClick = useCallback(() => {
    const w = defaultAiPanelWidth();
    setAiPanelWidth(w);
    localStorage.setItem('aiPanelWidth', String(w));
  }, []);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Broker Data Import state
  const [isBrokerImportOpen, setIsBrokerImportOpen] = useState(false);

  // Trading Journal state
  const [isJournalOpen, setIsJournalOpen] = useState(false);

  // Insights Dashboard state
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  // AI Review state
  const [isAiReviewOpen, setIsAiReviewOpen] = useState(false);
  const [aiReviewInitialDates, setAiReviewInitialDates] = useState(null);

  // Profile Update state
  const [isProfileUpdateOpen, setIsProfileUpdateOpen] = useState(false);

  // Backtest Dialog state
  const [isBacktestOpen, setIsBacktestOpen] = useState(false);

  // Compute avgChangeHistory for the selected config (for passing to StockTable)
  const avgChangeHistory = React.useMemo(() => {
    const history = {};
    Object.keys(avgChangeHistoryByConfig).forEach(symbol => {
      history[symbol] = avgChangeHistoryByConfig[symbol]?.[selectedConfig.key] || [];
    });
    return history;
  }, [avgChangeHistoryByConfig, selectedConfig]);

  // Compute AI picks symbols (union of Claude and OpenAI top picks)
  const aiPicksSymbols = React.useMemo(() => {
    const symbols = new Set();
    if (aiAnthropicResult?.topPicks) {
      aiAnthropicResult.topPicks.forEach(pick => symbols.add(pick.symbol));
    }
    if (aiOpenaiResult?.topPicks) {
      aiOpenaiResult.topPicks.forEach(pick => symbols.add(pick.symbol));
    }
    return symbols;
  }, [aiAnthropicResult, aiOpenaiResult]);

  // Compute stocks for display (filtered when in REST limited mode or AI picks mode)
  const stocksForDisplay = React.useMemo(() => {
    let stocks = allStocks;

    if (showAiPicksOnly && aiPicksSymbols.size > 0) {
      // When showing AI picks, show ALL AI picks regardless of REST limit
      // This ensures all AI-recommended stocks are visible
      stocks = allStocks.filter(s => aiPicksSymbols.has(s.symbol));
    } else if (isRestLimitedMode) {
      // In REST limited mode (not showing AI picks), only show the top stocks by Daily Gain
      stocks = [...allStocks]
        .filter(s => s.dailyGainPercentage > 0)
        .sort((a, b) => b.dailyGainPercentage - a.dailyGainPercentage)
        .slice(0, REST_POLL_LIMIT);
    }

    return stocks;
  }, [allStocks, isRestLimitedMode, showAiPicksOnly, aiPicksSymbols]);

  // Sync displayedStocks when stocksForDisplay changes
  useEffect(() => {
    if (stocksForDisplay.length > 0 && currentPage > 0) {
      const startIndex = (currentPage - 1) * STOCKS_PER_PAGE;
      const endIndex = startIndex + STOCKS_PER_PAGE;
      setDisplayedStocks(stocksForDisplay.slice(startIndex, endIndex));
    }
  }, [stocksForDisplay, currentPage]);

  // Close strategy menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (strategyMenuRef.current && !strategyMenuRef.current.contains(event.target)) {
        setIsStrategyMenuOpen(false);
      }
    };

    if (isStrategyMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStrategyMenuOpen]);

  // Close config menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (configMenuRef.current && !configMenuRef.current.contains(event.target)) {
        setIsConfigMenuOpen(false);
      }
    };

    if (isConfigMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isConfigMenuOpen]);

  // Phase 2: Learning Loop - Load user profile from backend DB
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profileResp = await apiGetProfile();
        if (profileResp?.profileData) {
          const insights = typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData;
          if (insights?.userProfile) {
            setUserProfile(insights.userProfile);
            console.log('✅ Loaded user profile for personalized scoring:', {
              winRate: insights.quickStats?.winRate,
              patterns: insights.userProfile?.successfulPatterns?.length || 0,
              trends: insights.userProfile?.successfulTrends?.length || 0
            });
          }
        }
      } catch (error) {
        console.log('ℹ️ No user profile found, using base score');
      }
    };

    loadUserProfile();
  }, []);

  // Update NY time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentNyTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Apply backend-computed market context (single source of truth).
  // ctx shapes (fetchMarketContext post-5/16):
  //   • success: {indices, marketSentiment, spreadClass, iwmSpyspread, trendScore, ...}
  //   • error:   {errorType: 'AUTH_EXPIRED' | 'NETWORK_OFFLINE' | ...}
  //   • null:    legacy callers, treated as generic error
  const applyMarketContext = (ctx) => {
    if (ctx && !ctx.errorType && ctx.indices !== undefined) {
      setMarketIndices(ctx.indices || []);
      setMarketSentiment(ctx.marketSentiment || 'loading');
      setSpreadClass(ctx.spreadClass || null);
      setIwmSpyspread(ctx.iwmSpyspread != null ? ctx.iwmSpyspread : null);
      setTrendScore(ctx.trendScore != null ? ctx.trendScore : null);
      setMarketErrorType(null);
      setMarketContextUpdatedAt(Date.now());
    } else {
      setMarketSentiment('error');
      setSpreadClass(null);
      setIwmSpyspread(null);
      setTrendScore(null);
      setMarketErrorType((ctx && ctx.errorType) || 'UNKNOWN');
    }
  };


  // Get recommended strategy based on market sentiment
  const getRecommendedStrategy = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return { key: 'aggressive', name: 'Aggressive' };
      case 'mixed': return { key: 'balanced', name: 'Balanced' };
      case 'bearish': return { key: 'conservative', name: 'Conservative' };
      default: return null;
    }
  };

  // Get data readiness for a config
  const getDataReadiness = useCallback((config, stocks) => {
    if (!stocks || stocks.length === 0) return { ready: 0, total: config.total, percent: 0 };
    // Use first stock's priceHistory length as reference
    const dataPoints = stocks[0]?.priceHistory?.length || 0;
    const percent = Math.min(100, Math.round((dataPoints / config.total) * 100));
    return { ready: dataPoints, total: config.total, percent };
  }, []);

  // Get acceleration arrow using selected config
  const getAccelerationArrow = useCallback((history) => {
    const accelConfig = getAccelerationConfig(selectedConfig.key);
    return getAccelerationArrowWithConfig(history, accelConfig);
  }, [selectedConfig]);

  // Refresh market context every 60 seconds (initial fetch is done in initializeStocks)
  // Backend is single source of truth for sentiment + spread; UI just renders.
  useEffect(() => {
    const interval = setInterval(async () => {
      const ctx = await fetchMarketContext();
      applyMarketContext(ctx);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Dedicated market status polling - runs every 30 seconds regardless of other state changes
  // This ensures market status transitions (extended -> open, open -> closed) are detected
  useEffect(() => {
    const interval = setInterval(async () => {
      const status = await fetchMarketStatus();
      if (status) {
        setMarketStatus(status);
      }
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Poll backend /api/health every 30s so the top status chips reflect
  // current server + Polygon upstream health. Fetch failure flips
  // backendReachable=false which renders 🔴 Backend offline.
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/api/health`,
                                 { cache: 'no-store' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        setBackendHealth(json);
        setBackendReachable(true);
      } catch (err) {
        setBackendReachable(false);
      }
    };
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get score for a stock (includes personalized adjustment)
  // Note: For sorting, only market sentiment adjustment is used (no AI results yet)
  const getStockScore = useCallback((stock) => {
    const configKey = selectedConfig.key;
    const historyByConfig = avgChangeHistoryByConfig[stock.symbol] || {};
    const history = historyByConfig[configKey] || [];
    const arrow = getAccelerationArrow(history);
    // Use the avgChange for the selected config
    const avgChange = avgChangeByConfig[stock.symbol]?.[configKey] ?? stock.avgChangePercent ?? 0;
    const scoreData = calculateStockScore(
      stock.dailyGainPercentage || 0,
      avgChange,
      arrow,
      stock.relativeVolume
    );

    // Phase 2: Learning Loop - Add personalized adjustment for sorting
    // For sorting, only use market sentiment (no AI results available yet)
    const personalization = calculatePersonalizedAdjustment(userProfile, marketSentiment, null);
    const finalScore = Math.max(0, Math.min(13, scoreData.points + personalization.adjustment));

    return finalScore;
  }, [avgChangeHistoryByConfig, avgChangeByConfig, selectedConfig, getAccelerationArrow, userProfile, marketSentiment]);

  // Helper function to compare two values for a single field
  const compareByField = useCallback((a, b, field, order) => {
    let aVal, bVal;
    const configKey = selectedConfig.key;

    // Special handling for score field (calculated dynamically)
    if (field === 'score') {
      aVal = getStockScore(a);
      bVal = getStockScore(b);
    } else if (field === 'avgChangePercent') {
      // Use the avgChange for the selected config
      aVal = avgChangeByConfig[a.symbol]?.[configKey] ?? a.avgChangePercent ?? 0;
      bVal = avgChangeByConfig[b.symbol]?.[configKey] ?? b.avgChangePercent ?? 0;
    } else {
      aVal = a[field];
      bVal = b[field];
    }

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Special handling for avgChangePercent: positive values before negative
    if (field === 'avgChangePercent') {
      if ((aVal >= 0 && bVal < 0) || (aVal < 0 && bVal >= 0)) {
        return bVal - aVal;
      }
    }

    const result = aVal - bVal;
    return order === 'asc' ? result : -result;
  }, [getStockScore, selectedConfig, avgChangeByConfig]);

  // Multi-column sort function
  const sortStocks = useCallback((stocks) => {
    return [...stocks].sort((a, b) => {
      for (const criterion of sortCriteria) {
        const result = compareByField(a, b, criterion.field, criterion.order);
        if (result !== 0) return result;
      }
      return 0;
    });
  }, [sortCriteria, compareByField]);

  const updateDisplayedStocks = useCallback((stocks, page) => {
    const startIndex = (page - 1) * STOCKS_PER_PAGE;
    const endIndex = startIndex + STOCKS_PER_PAGE;
    setDisplayedStocks(stocks.slice(startIndex, endIndex));
    setCurrentPage(page);
  }, []);

  const initializeStocks = useCallback(async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      setIsLoading(true);
      setError(null);

      // 1. First fetch market status and wait for it
      console.log('Fetching market status...');
      const status = await fetchMarketStatus();
      setMarketStatus(status);

      // 2. Then fetch market context (backend is source of truth)
      console.log('Fetching market context...');
      const ctx = await fetchMarketContext();
      applyMarketContext(ctx);

      // 3. Now start loading stocks
      console.log('Starting stock initialization...');
      const activeStocks = await fetchActiveStocks(10_000); // Fetch top 10000 active stocks

      // Phase 1: Filter stocks
      setLoadingProgress({ phase: 1, phaseName: 'Filtering stocks', current: 0, total: activeStocks.length });
      const filteredStocks = await filterStocks(
        activeStocks,
        { minMarketCap: 2_000_000_000, minEmployees: 50 }, // 2 billion market cap, 50 employees
        10_000, // limit to 10000 stocks
        (current, total) => setLoadingProgress({ phase: 1, phaseName: 'Filtering stocks', current, total })
      );

      // Phase 2: Initialize price data
      const totalStocks = filteredStocks.length;
      setLoadingProgress({ phase: 2, phaseName: 'Loading prices', current: 0, total: totalStocks });
      console.log(`Starting batch initialization for ${totalStocks} stocks (batch size: ${INIT_BATCH_SIZE}, delay: ${INIT_BATCH_DELAY}ms)`);

      const allInitializedStocks = [];

      for (let i = 0; i < totalStocks; i += INIT_BATCH_SIZE) {
        const batch = filteredStocks.slice(i, i + INIT_BATCH_SIZE);
        const batchNumber = Math.floor(i / INIT_BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(totalStocks / INIT_BATCH_SIZE);

        console.log(`Processing batch ${batchNumber}/${totalBatches} (stocks ${i + 1}-${Math.min(i + INIT_BATCH_SIZE, totalStocks)})`);

        const batchResults = await Promise.all(
          batch.map(async (stock) => {
            try {
              const [lastTrade, previousClose] = await Promise.all([
                fetchLastTrade(stock.symbol),
                fetchPreviousClose(stock.symbol),
              ]);

              if (lastTrade && previousClose) {
                const dailyGainPercentage = ((lastTrade.price - previousClose.close) / previousClose.close) * 100;
                const capRatio = stock.marketCap / stock.employees;
                const priceHistory = [{ price: lastTrade.price, timestamp: Date.now() }];

                // Calculate avgChangePercent for the default config (for backwards compatibility)
                const avgChangePercent = calculateAvgChangePercent(
                  priceHistory,
                  DEFAULT_CONFIG.total,
                  DEFAULT_CONFIG.recent
                );

                return {
                  ...stock,
                  currentPrice: lastTrade.price,
                  previousClose: previousClose.close,
                  dailyGainPercentage,
                  capRatio,
                  priceHistory,
                  avgChangePercent
                };
              }
              console.warn(`Incomplete data for ${stock.symbol}. Skipping.`);
              return null;
            } catch (error) {
              console.error(`Error processing data for ${stock.symbol}:`, error);
              return null;
            }
          })
        );

        // Add valid stocks from this batch
        const validBatchStocks = batchResults.filter(stock => stock !== null);
        allInitializedStocks.push(...validBatchStocks);

        // Update progress
        setLoadingProgress({ phase: 2, phaseName: 'Loading prices', current: Math.min(i + INIT_BATCH_SIZE, totalStocks), total: totalStocks });

        // Delay before next batch (except for the last batch)
        if (i + INIT_BATCH_SIZE < totalStocks) {
          await new Promise(resolve => setTimeout(resolve, INIT_BATCH_DELAY));
        }
      }

      console.log(`Batch initialization complete. ${allInitializedStocks.length} stocks loaded successfully.`);

      // Phase 3: Fetch today's open for gap calculation
      console.log('Fetching today open prices for gap calculation...');
      const allSymbols = allInitializedStocks.map(s => s.symbol);
      const SNAPSHOT_BATCH = 100;
      const allSnapshots = {};
      for (let i = 0; i < allSymbols.length; i += SNAPSHOT_BATCH) {
        const chunk = allSymbols.slice(i, i + SNAPSHOT_BATCH);
        const snapshots = await fetchBatchSnapshots(chunk);
        Object.assign(allSnapshots, snapshots);
      }

      // Merge todayOpen + gapPercent into each stock
      allInitializedStocks.forEach(stock => {
        const snap = allSnapshots[stock.symbol];
        if (snap?.todayOpen && stock.previousClose > 0) {
          stock.todayOpen = snap.todayOpen;
          stock.gapPercent = parseFloat(
            (((snap.todayOpen - stock.previousClose) / stock.previousClose) * 100).toFixed(2)
          );
        } else {
          stock.todayOpen = null;
          stock.gapPercent = null;
        }
      });

      // Initialize avgChangeByConfig for all configs
      const initialAvgChangeByConfig = {};
      allInitializedStocks.forEach(stock => {
        initialAvgChangeByConfig[stock.symbol] = {};
        PRICE_CONFIGS.forEach(config => {
          initialAvgChangeByConfig[stock.symbol][config.key] = calculateAvgChangePercent(
            stock.priceHistory,
            config.total,
            config.recent
          );
        });
      });
      setAvgChangeByConfig(initialAvgChangeByConfig);

      const sortedStocks = sortStocks(allInitializedStocks);
      setAllStocks(sortedStocks);
      updateDisplayedStocks(sortedStocks, 1);
    } catch (error) {
      console.error('Error initializing stocks:', error);
      setError('Failed to load stock data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [sortStocks, updateDisplayedStocks]);

  const updateData = useCallback(async () => {
    if (allStocks.length === 0) return;

    try {
      const status = await fetchMarketStatus();
      setMarketStatus(status);

      // Always collect data when app is running, regardless of market status
      const currentTime = Date.now();

      // Always limit REST polling to avoid browser resource exhaustion
      // REST is only used when market is closed (WebSocket handles open/extended-hours)
      const stocksToPoll = [...allStocks]
        .filter(s => s.dailyGainPercentage > 0)
        .sort((a, b) => b.dailyGainPercentage - a.dailyGainPercentage)
        .slice(0, REST_POLL_LIMIT);

      // Set REST limited mode (reset to page 1 on first entry)
      setIsRestLimitedMode(prev => {
        if (!prev) {
          setCurrentPage(1);
        }
        return true;
      });

      // Sequential requests with small delay (respects browser connection limits)
      const DELAY_MS = 15; // 15ms between requests (~66 req/s, safe for browser)

      const updatedStocksMap = new Map();
      for (const stock of stocksToPoll) {
        try {
          const lastTrade = await fetchLastTrade(stock.symbol);
          if (lastTrade) {
            const newPricePoint = {
              price: lastTrade.price,
              timestamp: currentTime
            };
            const updatedPriceHistory = [...stock.priceHistory, newPricePoint]
              .slice(-MAX_DATA_POINTS);
            const dailyGainPercentage = ((lastTrade.price - stock.previousClose) / stock.previousClose) * 100;

            const avgChangePercent = calculateAvgChangePercent(
              updatedPriceHistory,
              selectedConfig.total,
              selectedConfig.recent
            );

            updatedStocksMap.set(stock.symbol, {
              ...stock,
              currentPrice: lastTrade.price,
              dailyGainPercentage,
              avgChangePercent,
              priceHistory: updatedPriceHistory
            });
          }
        } catch (error) {
          // Keep original stock on error
        }
        await new Promise(resolve => setTimeout(resolve, DELAY_MS));
      }

      // Merge updated stocks back into allStocks
      const updatedStocks = allStocks.map(stock =>
        updatedStocksMap.get(stock.symbol) || stock
      );

      // Update avgChangeByConfig only for polled stocks
      const newAvgChangeByConfig = { ...avgChangeByConfig };
      const newAvgChangeHistoryByConfig = { ...avgChangeHistoryByConfig };

      // Only update stocks that were actually polled
      updatedStocksMap.forEach((stock, symbol) => {
        if (!newAvgChangeByConfig[symbol]) {
          newAvgChangeByConfig[symbol] = {};
        }
        if (!newAvgChangeHistoryByConfig[symbol]) {
          newAvgChangeHistoryByConfig[symbol] = {};
        }

        PRICE_CONFIGS.forEach(config => {
          // Calculate avgChange for this config
          const avgChange = calculateAvgChangePercent(
            stock.priceHistory,
            config.total,
            config.recent
          );
          newAvgChangeByConfig[symbol][config.key] = avgChange;

          // Update history for this config
          const accelConfig = getAccelerationConfig(config.key);
          const history = newAvgChangeHistoryByConfig[symbol][config.key] || [];
          const updatedHistory = [...history, avgChange].slice(-accelConfig.interval);
          newAvgChangeHistoryByConfig[symbol][config.key] = updatedHistory;
        });
      });

      setAvgChangeByConfig(newAvgChangeByConfig);
      setAvgChangeHistoryByConfig(newAvgChangeHistoryByConfig);

      // Always update allStocks with new data (for data collection)
      // But only sort and update display if not paused
      if (isUIPaused) {
        // Just store the unsorted updated data
        setAllStocks(updatedStocks);
      } else {
        const sortedStocks = sortStocks(updatedStocks);
        setAllStocks(sortedStocks);
        updateDisplayedStocks(sortedStocks, currentPage);
      }

    } catch (error) {
      console.error('Error updating data:', error);
    }
  }, [allStocks, currentPage, sortStocks, updateDisplayedStocks, avgChangeByConfig, avgChangeHistoryByConfig, isUIPaused, selectedConfig]);

  // Handle WebSocket price updates
  const handleWebSocketUpdate = useCallback((data) => {
    const { symbol, price, timestamp } = data;

    setAllStocks(prevStocks => {
      const stockIndex = prevStocks.findIndex(s => s.symbol === symbol);
      if (stockIndex === -1) return prevStocks;

      const stock = prevStocks[stockIndex];
      const newPricePoint = { price, timestamp: timestamp || Date.now() };
      const updatedPriceHistory = [...stock.priceHistory, newPricePoint]
        .slice(-MAX_DATA_POINTS);

      const dailyGainPercentage = ((price - stock.previousClose) / stock.previousClose) * 100;
      const avgChangePercent = calculateAvgChangePercent(
        updatedPriceHistory,
        selectedConfig.total,
        selectedConfig.recent
      );

      const updatedStock = {
        ...stock,
        currentPrice: price,
        dailyGainPercentage,
        avgChangePercent,
        priceHistory: updatedPriceHistory
      };

      const newStocks = [...prevStocks];
      newStocks[stockIndex] = updatedStock;
      return newStocks;
    });

    // Update avgChangeByConfig for all configs using the updated priceHistory
    setAllStocks(currentStocks => {
      const stock = currentStocks.find(s => s.symbol === symbol);
      if (stock) {
        setAvgChangeByConfig(prev => {
          const newState = { ...prev };
          if (!newState[symbol]) newState[symbol] = {};

          // Calculate avgChange for all configs
          PRICE_CONFIGS.forEach(config => {
            newState[symbol][config.key] = calculateAvgChangePercent(
              stock.priceHistory,
              config.total,
              config.recent
            );
          });

          return newState;
        });
      }
      return currentStocks; // Return unchanged
    });
  }, [selectedConfig]);

  // Initialize WebSocket connection
  const initWebSocket = useCallback((stocks) => {
    if (!useWebSocket || stocks.length === 0) return;

    const symbols = stocks.map(s => s.symbol);
    console.log(`Initializing WebSocket for ${symbols.length} stocks`);

    const ws = new StockWebSocket(handleWebSocketUpdate, setWsConnected, setRestFallbackActive);
    wsRef.current = ws;

    ws.connect()
      .then(() => {
        console.log('WebSocket connected, subscribing to stocks...');
        ws.subscribe(symbols);
      })
      .catch(err => {
        console.error('WebSocket connection failed:', err);
        console.log('Falling back to REST polling');
        setUseWebSocket(false);
      });
  }, [useWebSocket, handleWebSocketUpdate]);

  // Initialize stocks only once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('Configuration:', {
      DEFAULT_CONFIG,
      UPDATE_INTERVAL
    });

    initializeStocks();
  }, []); // Empty dependency - initializeStocks has internal guard (initializationRef)

  // Separate cleanup effect - only runs on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        console.log('Component unmounting, disconnecting WebSocket...');
        wsRef.current.disconnect();
      }
    };
  }, []); // Empty dependency - only cleanup on unmount

  // Start WebSocket after stocks are loaded (only when market is active)
  useEffect(() => {
    if (allStocks.length > 0 && useWebSocket && !wsRef.current && marketStatus) {
      const isMarketActive = marketStatus.market === 'open' || marketStatus.market === 'extended-hours';

      if (isMarketActive) {
        // Market is active: connect WebSocket
        console.log(`Market is ${marketStatus.market} - connecting WebSocket`);
        setIsRestLimitedMode(false);
        initWebSocket(allStocks);
      } else {
        // Market closed: use REST only
        console.log(`Market is ${marketStatus.market} - using REST only`);
        setUseWebSocket(false);
      }
    }
  }, [allStocks, useWebSocket, initWebSocket, marketStatus]);

  // Sync market status with WebSocket service AND handle market transitions
  useEffect(() => {
    if (marketStatus && allStocks.length > 0) {
      const isActive = marketStatus.market === 'open' || marketStatus.market === 'extended-hours';

      // Notify WebSocket service of market status
      if (wsRef.current) {
        wsRef.current.setMarketOpen(isActive);
      }

      // When market closes and WebSocket is connected, disconnect and switch to REST
      if (!isActive && wsRef.current && useWebSocket) {
        console.log('Market closed - disconnecting WebSocket and switching to REST');
        wsRef.current.disconnect();
        wsRef.current = null;
        setUseWebSocket(false);
        setIsRestLimitedMode(true);
      }

      // When market reopens and we're in REST mode (or fallback active), switch back to WebSocket
      if (isActive && (!useWebSocket || restFallbackActive) && !wsRef.current) {
        console.log(`Market active (${marketStatus.market}) - (re)connecting WebSocket`);
        setUseWebSocket(true);
        setRestFallbackActive(false);  // Reset fallback state to allow fresh WebSocket connection
        setIsRestLimitedMode(false);
        // WebSocket will be initialized by the other effect (line 970-984)
      }
    }
  }, [marketStatus, useWebSocket, restFallbackActive, allStocks.length]);

  // Fallback to REST polling if WebSocket is disabled OR WebSocket failed
  useEffect(() => {
    const shouldUseRest = !useWebSocket || restFallbackActive;
    if (shouldUseRest && allStocks.length > 0) {
      // Only log once when entering REST mode
      if (!hasLoggedRestMode.current) {
        console.log(`Using REST polling ${restFallbackActive ? '(WebSocket fallback)' : '(manual mode)'}`);
        hasLoggedRestMode.current = true;
      }
      const dataInterval = setInterval(() => {
        updateData();
      }, UPDATE_INTERVAL);

      return () => clearInterval(dataInterval);
    } else {
      // Reset when exiting REST mode
      hasLoggedRestMode.current = false;
    }
  }, [useWebSocket, restFallbackActive, allStocks.length, updateData]);

  // Periodic sort and update display (for WebSocket mode)
  // Note: Market status polling is handled by dedicated effect above
  useEffect(() => {
    if (useWebSocket && allStocks.length > 0) {
      const sortInterval = setInterval(() => {
        // Update avgChangeByConfig for all configs (WebSocket mode)
        const newAvgChangeByConfig = { ...avgChangeByConfig };
        const newAvgChangeHistoryByConfig = { ...avgChangeHistoryByConfig };

        allStocks.forEach(stock => {
          if (!newAvgChangeByConfig[stock.symbol]) {
            newAvgChangeByConfig[stock.symbol] = {};
          }
          if (!newAvgChangeHistoryByConfig[stock.symbol]) {
            newAvgChangeHistoryByConfig[stock.symbol] = {};
          }

          PRICE_CONFIGS.forEach(config => {
            const avgChange = calculateAvgChangePercent(
              stock.priceHistory,
              config.total,
              config.recent
            );
            newAvgChangeByConfig[stock.symbol][config.key] = avgChange;

            const accelConfig = getAccelerationConfig(config.key);
            const history = newAvgChangeHistoryByConfig[stock.symbol][config.key] || [];
            const updatedHistory = [...history, avgChange].slice(-accelConfig.interval);
            newAvgChangeHistoryByConfig[stock.symbol][config.key] = updatedHistory;
          });
        });

        setAvgChangeByConfig(newAvgChangeByConfig);
        setAvgChangeHistoryByConfig(newAvgChangeHistoryByConfig);

        // Skip UI update if paused (data collection continues via WebSocket)
        if (isUIPaused) return;

        const sortedStocks = sortStocks(allStocks);
        setAllStocks(sortedStocks);
        updateDisplayedStocks(sortedStocks, currentPage);
      }, UPDATE_INTERVAL);

      return () => clearInterval(sortInterval);
    }
  }, [useWebSocket, allStocks, currentPage, sortStocks, updateDisplayedStocks, isUIPaused, avgChangeByConfig, avgChangeHistoryByConfig]);

  // Handle strategy change from dropdown
  const handleStrategyChange = useCallback((strategyKey) => {
    const strategy = SORT_STRATEGIES[strategyKey];
    if (!strategy) return;

    setCurrentStrategy(strategyKey);
    setSortCriteria(strategy.criteria);
    setIsStrategyMenuOpen(false);

    // Apply sort immediately
    const sortedStocks = [...allStocks].sort((a, b) => {
      for (const criterion of strategy.criteria) {
        const result = compareByField(a, b, criterion.field, criterion.order);
        if (result !== 0) return result;
      }
      return 0;
    });
    setAllStocks(sortedStocks);
    updateDisplayedStocks(sortedStocks, currentPage);
  }, [allStocks, currentPage, compareByField, updateDisplayedStocks]);

  // Handle config change from dropdown
  const handleConfigChange = useCallback((configKey) => {
    const config = PRICE_CONFIGS.find(c => c.key === configKey);
    if (!config) return;

    setSelectedConfig(config);
    setIsConfigMenuOpen(false);

    // Re-sort with the new config's avgChange values
    const sortedStocks = sortStocks(allStocks);
    setAllStocks(sortedStocks);
    updateDisplayedStocks(sortedStocks, currentPage);
  }, [allStocks, currentPage, sortStocks, updateDisplayedStocks]);

  // Handle sort: click = primary, shift+click = add secondary/tertiary (max 3)
  const handleSort = useCallback((field, shiftKey = false) => {
    setSortCriteria(prevCriteria => {
      const existingIndex = prevCriteria.findIndex(c => c.field === field);
      let newCriteria;

      if (shiftKey) {
        // Shift+click: add or toggle existing column
        if (existingIndex >= 0) {
          // Toggle order of existing column
          newCriteria = prevCriteria.map((c, i) =>
            i === existingIndex ? { ...c, order: c.order === 'asc' ? 'desc' : 'asc' } : c
          );
        } else if (prevCriteria.length < 3) {
          // Add new column (max 3)
          newCriteria = [...prevCriteria, { field, order: 'desc' }];
        } else {
          // Already at max, replace last column
          newCriteria = [...prevCriteria.slice(0, 2), { field, order: 'desc' }];
        }
      } else {
        // Normal click: set as primary sort
        if (existingIndex === 0) {
          // Already primary, toggle order
          newCriteria = [{ field, order: prevCriteria[0].order === 'asc' ? 'desc' : 'asc' }];
        } else {
          // Set as new primary
          newCriteria = [{ field, order: 'desc' }];
        }
      }

      // Apply sort immediately
      const sortedStocks = [...allStocks].sort((a, b) => {
        for (const criterion of newCriteria) {
          const result = compareByField(a, b, criterion.field, criterion.order);
          if (result !== 0) return result;
        }
        return 0;
      });
      setAllStocks(sortedStocks);
      updateDisplayedStocks(sortedStocks, currentPage);

      return newCriteria;
    });
  }, [allStocks, currentPage, compareByField, updateDisplayedStocks]);

  const changePage = useCallback((newPage) => {
    updateDisplayedStocks(allStocks, newPage);
  }, [allStocks, updateDisplayedStocks]);

  // Toggle UI pause state - when resuming, immediately sort and update display
  const toggleUIPause = useCallback(() => {
    setIsUIPaused(prev => {
      const newPaused = !prev;
      if (!newPaused) {
        // Resuming - sort and update display with accumulated data
        const sortedStocks = sortStocks(allStocks);
        setAllStocks(sortedStocks);
        updateDisplayedStocks(sortedStocks, currentPage);
      }
      return newPaused;
    });
  }, [allStocks, currentPage, sortStocks, updateDisplayedStocks]);

  // Handle AI Analysis - Progressive Loading (parallel API calls)
  const handleAiAnalysis = useCallback(async () => {
    if (allStocks.length === 0) return;

    // Reset results and start all loading indicators
    // Abort any in-flight risk stream from a previous scan so the next one
    // can re-trigger; otherwise riskControllerRef.current would block it.
    if (riskControllerRef.current) {
      riskControllerRef.current.abort();
      riskControllerRef.current = null;
    }
    setAiAnthropicResult(null);
    setAiOpenaiResult(null);
    setRiskAssessment(null);
    setIsAnthropicLoading(true);
    setIsOpenaiLoading(true);
    setIsRiskLoading(false);
    setAiPayloadCache(null);
    setShowAiPicksOnly(false); // Reset filter when starting new analysis
    setAiScanTime(new Date().toISOString()); // Record scan time at start
    setIsScanSaved(false); // Reset save state for new scan

    // Get top stocks sorted by current criteria for AI analysis
    // Filter out stocks with very low volume (< 200K) to ensure liquidity
    const MIN_VOLUME_FOR_AI = 200000;
    const sortedStocks = sortStocks(allStocks);
    const liquidStocks = sortedStocks.filter(s => !s.todayVolume || s.todayVolume >= MIN_VOLUME_FOR_AI);
    const stocksForAi = liquidStocks.slice(0, aiStockCount);

    console.log(`📤 Sending ${stocksForAi.length} stocks to AI for progressive analysis`);

    const context = {
      config: selectedConfig.key,
      marketStatus: marketStatus?.market || 'unknown',
      marketSentiment: marketSentiment,
      indices: marketIndices,
      // userProfile loaded from DB by buildAiPayload automatically
    };

    // Build payload once (fetches Polygon data), then send to both AIs
    try {
      const payload = await buildAiPayload(stocksForAi, context);
      // Cache enriched stock data and context for risk assessment
      setAiPayloadCache({ stocks: payload.stocks, context: payload.context });

      // Progressive Loading: Call Claude and OpenAI in parallel with shared payload
      sendClaudeAnalysis(payload)
        .then(result => {
          console.log('✅ Claude analysis complete');
          setAiAnthropicResult(result);
        })
        .catch(error => {
          console.error('Claude analysis error:', error);
          setAiAnthropicResult({ provider: AI_PROVIDER.ANTHROPIC, error: error.message });
        })
        .finally(() => {
          setIsAnthropicLoading(false);
        });

      sendOpenaiAnalysis(payload)
        .then(result => {
          console.log('✅ OpenAI analysis complete');
          setAiOpenaiResult(result);
        })
        .catch(error => {
          console.error('OpenAI analysis error:', error);
          setAiOpenaiResult({ provider: AI_PROVIDER.OPENAI, error: error.message });
        })
        .finally(() => {
          setIsOpenaiLoading(false);
        });
    } catch (error) {
      console.error('Failed to build AI payload:', error);
      setIsAnthropicLoading(false);
      setIsOpenaiLoading(false);
      setAiAnthropicResult({ provider: AI_PROVIDER.ANTHROPIC, error: error.message });
      setAiOpenaiResult({ provider: AI_PROVIDER.OPENAI, error: error.message });
    }

  }, [allStocks, sortStocks, selectedConfig, marketStatus, marketSentiment, marketIndices, aiStockCount]);

  // Auto-trigger streaming Risk Assessment when both scan results are available.
  // Each SSE event flips one piece of riskAssessment state, so Top Picks /
  // Detailed Comparison / Risk Section all light up incrementally instead of
  // waiting ~50 s for the full reply.
  //
  // We keep the AbortController in riskControllerRef (NOT closed over by
  // useEffect cleanup) because every state update during streaming changes
  // the riskAssessment dep and would otherwise trigger cleanup → aborting
  // our own stream mid-flight. Unmount-only cleanup lives in the separate
  // useEffect below.
  useEffect(() => {
    const hasBothResults = aiAnthropicResult && aiOpenaiResult
      && !aiAnthropicResult.error && !aiOpenaiResult.error
      && !isAnthropicLoading && !isOpenaiLoading;

    if (riskControllerRef.current) return;  // a stream is already running
    if (!hasBothResults || riskAssessment || isRiskLoading || !aiPayloadCache) return;

    setIsRiskLoading(true);
    setRiskAssessment({
      provider: AI_PROVIDER.ANTHROPIC,
      stockRisks: [],
      conflicts: [],
      missedRisks: [],
      _streaming: true,
    });
    console.log('🛡️ Both scan results available - triggering streaming risk assessment');

    const controller = new AbortController();
    riskControllerRef.current = controller;

    (async () => {
      try {
        const payload = buildRiskPayload(
          aiAnthropicResult, aiOpenaiResult,
          aiPayloadCache.stocks, aiPayloadCache.context
        );
        for await (const event of streamRiskAssessment(payload, controller.signal)) {
          switch (event.type) {
            case 'overallRiskSummary':
              setRiskAssessment(prev => ({ ...prev, overallRiskSummary: event.data }));
              break;
            case 'stockRisk':
              setRiskAssessment(prev => ({
                ...prev,
                stockRisks: [...(prev?.stockRisks || []), event.data],
              }));
              break;
            case 'conflict':
              setRiskAssessment(prev => ({
                ...prev,
                conflicts: [...(prev?.conflicts || []), event.data],
              }));
              break;
            case 'missedRisk':
              setRiskAssessment(prev => ({
                ...prev,
                missedRisks: [...(prev?.missedRisks || []), event.data],
              }));
              break;
            case 'done':
              console.log('✅ Risk assessment streaming complete', event.data);
              setRiskAssessment(prev => ({ ...prev, _streaming: false }));
              setIsRiskLoading(false);
              break;
            case 'error':
              setRiskAssessment(prev => ({
                ...prev,
                error: event.data?.message || 'Risk assessment error',
                _streaming: false,
              }));
              setIsRiskLoading(false);
              break;
            default:
              break;
          }
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          console.log('Risk assessment stream aborted');
          return;
        }
        console.error('Risk assessment stream error:', e);
        setRiskAssessment({ provider: AI_PROVIDER.ANTHROPIC, error: e.message });
        setIsRiskLoading(false);
      } finally {
        // Clear the ref so a fresh scan can re-trigger.
        if (riskControllerRef.current === controller) {
          riskControllerRef.current = null;
        }
      }
    })();
  }, [aiAnthropicResult, aiOpenaiResult, isAnthropicLoading, isOpenaiLoading, riskAssessment, isRiskLoading, aiPayloadCache]);

  // Unmount-only cleanup: abort any in-flight risk stream. Saves Anthropic
  // tokens if the user closes the tab while ~50 s of generation is in flight.
  useEffect(() => () => {
    if (riskControllerRef.current) {
      riskControllerRef.current.abort();
      riskControllerRef.current = null;
    }
  }, []);

  // Manual save scan record handler
  // Save scan via /api/scan/save (2026-05-17 path A+).
  // Frontend sends raw AI results + analysis-time marketContext; backend builds
  // allRecommendedStocks via ScanDataUtils.buildRecommendedStocks (single
  // source of truth, no frontend duplication of merge logic).
  const handleSaveScan = useCallback(() => {
    const isComplete = riskAssessment && !riskAssessment.error && !isRiskLoading
      && aiAnthropicResult && !aiAnthropicResult.error
      && aiOpenaiResult && !aiOpenaiResult.error
    ;

    if (!isComplete || isScanSaved) return;

    const etTimestamp = formatInTimeZone(new Date(aiScanTime || Date.now()), ET_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ss");
    apiSaveScan({
      scanTimestamp: etTimestamp,
      marketContext: {
        marketSentiment: marketSentiment,
        indices: marketIndices?.map(i => ({ symbol: i.symbol, name: i.name, change: i.change })),
        config: selectedConfig?.key
      },
      anthropicResult: {
        provider: AI_PROVIDER.ANTHROPIC,
        // Model that produced this result — regime label for rule
        // discovery / backtesting segmentation. Auto scans already
        // record it (parseAiJsonResponse); manual saves were the only
        // path dropping it.
        model: aiAnthropicResult.model,
        summary: aiAnthropicResult.summary,
        topPicks: aiAnthropicResult.topPicks,
        avoid: aiAnthropicResult.avoid
      },
      openaiResult: {
        provider: AI_PROVIDER.OPENAI,
        model: aiOpenaiResult.model,
        summary: aiOpenaiResult.summary,
        topPicks: aiOpenaiResult.topPicks,
        avoid: aiOpenaiResult.avoid
      },
      riskAssessment: {
        stockRisks: riskAssessment.stockRisks,
        conflicts: riskAssessment.conflicts,
        overallRiskSummary: riskAssessment.overallRiskSummary
      },
    })
      .then(resp => {
        console.log(`✅ Saved scan record to DB:`, resp?.id, `(${resp?.stockCount} picks)`);
        setIsScanSaved(true);
      })
      .catch(err => console.error('Failed to save scan record:', err));
  }, [riskAssessment, isRiskLoading, aiAnthropicResult, aiOpenaiResult, aiScanTime, isScanSaved, marketSentiment, marketIndices, selectedConfig]);

  // Toggle AI Panel
  const toggleAiPanel = useCallback(() => {
    setIsAiPanelOpen(prev => !prev);
  }, []);

  // Toggle AI Picks Filter
  const toggleAiPicksFilter = useCallback(() => {
    setShowAiPicksOnly(prev => {
      if (!prev) {
        // When enabling filter, reset to page 1
        setCurrentPage(1);
      }
      return !prev;
    });
  }, []);

  if (isLoading) {
    const progressPercent = loadingProgress.total > 0
      ? Math.round((loadingProgress.current / loadingProgress.total) * 100)
      : 0;
    const loadingMarketDisplay = marketStatus ? getMarketStatusDisplay(marketStatus.market) : { emoji: '⚪', text: 'loading', color: '#6b7280' };
    const loadingNyTime = formatInTimeZone(currentNyTime, ET_TIMEZONE, 'HH:mm:ss');

    return (
      <div style={{ padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
        {/* Simplified status bar for loading screen */}
        <div style={{
          fontSize: '0.85em',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span>{loadingMarketDisplay.emoji}</span>
            <span style={{ color: loadingMarketDisplay.color, fontWeight: '500' }}>{loadingMarketDisplay.text}</span>
          </span>
          <span style={{ color: '#9ca3af' }}>|</span>
          <span style={{ color: '#6b7280' }}>{loadingNyTime} ET</span>
          <span style={{ color: '#9ca3af' }}>|</span>
          <span style={{ color: '#9ca3af' }}>{selectedConfig.key}</span>
        </div>
        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          Phase {loadingProgress.phase}/2: {loadingProgress.phaseName || 'Initializing'}...
        </div>
        {loadingProgress.total > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{
              width: '400px',
              height: '24px',
              backgroundColor: '#e0e0e0',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progressPercent}%`,
                height: '100%',
                backgroundColor: loadingProgress.phase === 1 ? '#2196F3' : '#4CAF50',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
              {loadingProgress.current.toLocaleString()} / {loadingProgress.total.toLocaleString()} stocks ({progressPercent}%)
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (allStocks.length === 0) {
    return <div>No stock data available. Please try again later.</div>;
  }

  // Prepare display values
  const dataSource = getDataSourceDisplay(useWebSocket, wsConnected, restFallbackActive);
  const marketDisplay = marketStatus ? getMarketStatusDisplay(marketStatus.market) : { emoji: '⚪', text: 'loading', color: '#6b7280' };
  const nyTime = formatInTimeZone(currentNyTime, ET_TIMEZONE, 'HH:mm:ss');

  // Get background color based on market status
  const getMarketStatusBackground = (status) => {
    switch (status) {
      case 'open': return { bg: '#f0fdf4', border: '#bbf7d0' }; // green
      case 'closed': return { bg: '#fef2f2', border: '#fecaca' }; // red
      case 'extended-hours': return { bg: '#fefce8', border: '#fef08a' }; // yellow
      default: return { bg: '#f9fafb', border: '#e5e7eb' }; // gray
    }
  };
  const marketStatusBg = marketStatus ? getMarketStatusBackground(marketStatus.market) : { bg: '#f9fafb', border: '#e5e7eb' };

  // Status chips for the new health row (Row 1). Derived from current state +
  // backend health snapshot polled every 30s.
  const marketChip  = buildMarketChip(marketStatus);
  const dataChip    = buildDataChip(useWebSocket, wsConnected, restFallbackActive);
  const backendChip = buildBackendChip(backendReachable);
  const polygonChip = buildPolygonChip(backendHealth, backendReachable,
                                       wsConnected, restFallbackActive, useWebSocket);

  return (
    <div style={{ padding: '1rem', fontFamily: 'Arial, sans-serif' }}>
      {/* Row 1: System status chips + clock + user / Sign Out (added 2026-06-04) */}
      <div style={{
        fontSize: '0.85em',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        padding: '8px 12px',
        backgroundColor: '#fafafa',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
      }}>
        <StatusChip {...marketChip} />
        <span style={{ color: '#d1d5db' }}>|</span>
        <StatusChip {...dataChip} />
        <span style={{ color: '#d1d5db' }}>|</span>
        <StatusChip {...backendChip} />
        <span style={{ color: '#d1d5db' }}>|</span>
        <StatusChip {...polygonChip} />
        <span style={{ color: '#d1d5db' }}>|</span>
        <span style={{ color: '#6b7280' }}>{nyTime} ET</span>
        <span style={{ flex: 1 }} />
        <span style={{ color: '#6b7280', marginRight: '4px' }}>{username}</span>
        <button
          onClick={onLogout}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: 'white',
            color: '#374151',
          }}
        >Sign Out</button>
      </div>

      {/* Market Indices Line — moved to Row 2 position (2026-06-04) */}
      <div style={{
        fontSize: '0.85em',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        padding: '8px 12px',
        backgroundColor: marketSentiment === 'bullish' ? '#f0fdf4' : marketSentiment === 'mixed' ? '#fefce8' : marketSentiment === 'bearish' ? '#fef2f2' : '#f9fafb',
        borderRadius: '6px',
        border: `1px solid ${marketSentiment === 'bullish' ? '#bbf7d0' : marketSentiment === 'mixed' ? '#fef08a' : marketSentiment === 'bearish' ? '#fecaca' : '#e5e7eb'}`,
      }}>
        {/* Market Sentiment — silent when backend errors (Row 1 chip already
            tells user "Backend offline"; old "Server offline" label here was
            redundant. Loading state stays so user knows init is in progress.) */}
        {marketSentiment !== 'error' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '1.1em' }}>
              {getSentimentEmoji(marketSentiment)}
            </span>
            <span style={{
              fontWeight: 'bold',
              color: getSentimentColor(marketSentiment)
            }}>
              {marketSentiment === 'loading' ? 'Loading...' : formatMarketSentiment(trendScore, marketSentiment)}
            </span>
          </span>
        )}

        {/* Market Breadth (IWM-SPY spread class) — plain text style to
            match the rest of Row 2 (sentiment + indices). Color signal
            stays in the text via getSpreadColor.text; the chip framing
            (bg/border/padding) was visually inconsistent with the row's
            other plain-text items. */}
        {spreadClass && (() => {
          const colors = getSpreadColor(spreadClass);
          return (
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}
              title={`IWM-SPY spread: ${iwmSpyspread != null && iwmSpyspread >= 0 ? '+' : ''}${iwmSpyspread != null ? iwmSpyspread.toFixed(2) : '0.00'}% — ${spreadClass}\n` +
                     `severe-narrow / mild-narrow: large caps outperforming small caps (risk-off rotation).\n` +
                     `balanced: normal market structure.\n` +
                     `small-cap-leading: small caps healthy (risk-on environment).`}
            >
              <span>{getSpreadEmoji(spreadClass)}</span>
              <span style={{ fontWeight: '500', color: colors.text }}>
                {spreadClass}
              </span>
              {iwmSpyspread != null && (
                <span style={{ fontWeight: 'bold', color: colors.text }}>
                  {iwmSpyspread >= 0 ? '+' : ''}{iwmSpyspread.toFixed(2)}%
                </span>
              )}
            </span>
          );
        })()}

        <span style={{ color: '#d1d5db' }}>|</span>

        {/* Individual Indices — when backend is in error (marketErrorType
            non-null) the indices we render are stale leftovers from the
            last successful fetch. Gray them out + prefix ⏸️ so the user
            can't mistake them for live data; tooltip carries the exact
            age in seconds / minutes / hours derived from currentNyTime
            (which updates every second). */}
        {marketIndices.length > 0 ? (() => {
          const isStale = marketErrorType !== null && marketContextUpdatedAt !== null;
          const ageMs = isStale ? Date.now() - marketContextUpdatedAt : 0;
          const ageLabel = (() => {
            const sec = Math.floor(ageMs / 1000);
            if (sec < 60) return `${sec}s ago`;
            const min = Math.floor(sec / 60);
            if (min < 60) return `${min} min ago`;
            const hr = Math.floor(min / 60);
            return `${hr} hr ago`;
          })();
          return marketIndices.map((index, i) => (
            <span
              key={index.symbol}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help', opacity: isStale ? 0.55 : 1 }}
              title={isStale ? `Backend offline — last updated ${ageLabel}` : index.description}
            >
              {isStale && <span style={{ marginRight: '2px' }}>⏸️</span>}
              <span style={{ fontWeight: '500', color: isStale ? '#9ca3af' : '#374151' }}>{index.name}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85em' }}>({index.symbol})</span>
              <span style={{
                color: isStale
                  ? '#9ca3af'
                  : (index.change !== null ? (index.change >= 0 ? '#16a34a' : '#dc2626') : '#6b7280'),
                fontWeight: '500'
              }}>
                {index.change !== null ? `${index.change >= 0 ? '+' : ''}${index.change.toFixed(2)}%` : 'N/A'}
              </span>
              {i < marketIndices.length - 1 && <span style={{ color: '#d1d5db', marginLeft: '8px' }}>|</span>}
            </span>
          ));
        })() : (
          <span style={{ color: '#6b7280' }}>Loading indices...</span>
        )}

        <span style={{ color: '#d1d5db' }}>|</span>

        {/* Recommended Strategy — plain text style to match the rest of
            Row 2. emoji + name preserved; the white chip framing was the
            other source of visual inconsistency in the row. */}
        {marketSentiment !== 'loading' && marketSentiment !== 'error' && getRecommendedStrategy(marketSentiment) && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#6b7280' }}>Rec:</span>
            <span style={{ fontWeight: '500', color: '#374151' }}>
              {SORT_STRATEGIES[getRecommendedStrategy(marketSentiment).key].emoji} {getRecommendedStrategy(marketSentiment).name}
            </span>
          </span>
        )}
      </div>

      {/* Function buttons row */}
      <div style={{
        fontSize: '0.85em',
        marginBottom: '4px',
        marginTop: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        padding: '6px 12px',
        backgroundColor: '#f8fafc',
        borderRadius: '6px',
        border: '1px solid #e2e8f0'
      }}>
        {/* AI Analysis Button */}
        <button
          onClick={toggleAiPanel}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isAiPanelOpen ? '#3b82f6' : 'white',
            color: isAiPanelOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>⚡</span>
          <span>AI Scan</span>
        </button>

        {/* Show AI Picks Filter Button - only show when AI results are available */}
        {aiPicksSymbols.size > 0 && (
          <button
            onClick={toggleAiPicksFilter}
            style={{
              padding: '4px 10px',
              fontSize: '0.9em',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: showAiPicksOnly ? '#8b5cf6' : 'white',
              color: showAiPicksOnly ? 'white' : '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span>🎯</span>
            <span>{showAiPicksOnly ? `AI Picks (${aiPicksSymbols.size})` : 'Show AI Picks'}</span>
          </button>
        )}

        <span style={{ color: '#d1d5db' }}>|</span>

        {/* Import Broker Data Button */}
        <button
          onClick={() => setIsBrokerImportOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: 'white',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>📥</span>
          <span>Broker Import</span>
        </button>

        {/* Trading Journal Button */}
        <button
          onClick={() => setIsJournalOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isJournalOpen ? '#8b5cf6' : 'white',
            color: isJournalOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>📔</span>
          <span>Trade Log</span>
        </button>

        <span style={{ color: '#d1d5db' }}>|</span>

        {/* Backtest Button */}
        <button
          onClick={() => setIsBacktestOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isBacktestOpen ? '#667eea' : 'white',
            color: isBacktestOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>🧪</span>
          <span>Backtest</span>
        </button>

        {/* AI Review Button */}
        <button
          onClick={() => setIsAiReviewOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isAiReviewOpen ? '#667eea' : 'white',
            color: isAiReviewOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>🤖</span>
          <span>AI Review</span>
        </button>

        {/* AI Profile Button */}
        <button
          onClick={() => setIsProfileUpdateOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isProfileUpdateOpen ? '#667eea' : 'white',
            color: isProfileUpdateOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>👤</span>
          <span>AI Profile</span>
        </button>

        {/* Insights Dashboard Button */}
        <button
          onClick={() => setIsInsightsOpen(true)}
          style={{
            padding: '4px 10px',
            fontSize: '0.9em',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: isInsightsOpen ? '#667eea' : 'white',
            color: isInsightsOpen ? 'white' : '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>📊</span>
          <span>AI Insights</span>
        </button>

        <span style={{ flex: 1 }} />

        {/* Strategy + Config + stocks count (moved from old Row 2, 2026-06-04) */}
        <div ref={strategyMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setIsStrategyMenuOpen(!isStrategyMenuOpen)}
            style={{
              padding: '4px 10px',
              fontSize: '0.9em',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span>{SORT_STRATEGIES[currentStrategy].emoji}</span>
            <span>{SORT_STRATEGIES[currentStrategy].name}</span>
            <span style={{ fontSize: '0.8em' }}>▼</span>
          </button>

          {isStrategyMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '200px'
            }}>
              {Object.entries(SORT_STRATEGIES).map(([key, strategy]) => (
                <div
                  key={key}
                  onClick={() => handleStrategyChange(key)}
                  title={strategy.description}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: currentStrategy === key ? '#eff6ff' : 'transparent',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentStrategy === key ? '#eff6ff' : '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentStrategy === key ? '#eff6ff' : 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{strategy.emoji}</span>
                    <span style={{ fontWeight: currentStrategy === key ? 'bold' : 'normal' }}>
                      {strategy.name}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '0.85em' }}>
                      {strategy.shorthand}
                    </span>
                  </div>
                  {currentStrategy === key && <span style={{ color: '#22c55e' }}>✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <span style={{ color: '#9ca3af' }}>|</span>

        <span style={{ color: '#4b5563' }}>
          {stocksForDisplay.length} stocks{isRestLimitedMode ? ` (of ${allStocks.length})` : ''}
        </span>

        <span style={{ color: '#9ca3af' }}>|</span>

        <div ref={configMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setIsConfigMenuOpen(!isConfigMenuOpen)}
            style={{
              padding: '4px 10px',
              fontSize: '0.9em',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {(() => {
              const readiness = getDataReadiness(selectedConfig, allStocks);
              return (
                <>
                  {readiness.percent < 100 ? '⚠️' : '✅'}
                  <span>{selectedConfig.key}</span>
                  <span style={{ color: '#6b7280' }}>{selectedConfig.label}</span>
                  <span style={{ fontSize: '0.8em' }}>▼</span>
                </>
              );
            })()}
          </button>

          {isConfigMenuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '220px'
            }}>
              {PRICE_CONFIGS.map((config) => {
                const readiness = getDataReadiness(config, allStocks);
                const isSelected = selectedConfig.key === config.key;
                return (
                  <div
                    key={config.key}
                    onClick={() => handleConfigChange(config.key)}
                    title={config.description}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid #f3f4f6'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{readiness.percent >= 100 ? '✅' : readiness.percent >= 50 ? '⚠️' : '❌'}</span>
                      <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                        {config.key}
                      </span>
                      <span style={{ color: '#6b7280', fontSize: '0.85em' }}>
                        {config.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        fontSize: '0.75em',
                        color: readiness.percent >= 100 ? '#16a34a' : readiness.percent >= 50 ? '#ca8a04' : '#dc2626'
                      }}>
                        ({readiness.ready}/{readiness.total})
                      </span>
                      {isSelected && <span style={{ color: '#22c55e' }}>✓</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main content area with flex layout */}
      <div style={{ display: 'flex', gap: '0', height: 'calc(100vh - 160px)' }}>
        {/* Left side: Stock table and pagination */}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'auto' }}>
          <StockTable
            stocks={displayedStocks}
            sortCriteria={sortCriteria}
            onSort={handleSort}
            marketStatus={marketStatus}
            priceHistoryMinutes={90}
            pricePointIntervalSeconds={30}
            avgChangeHistory={avgChangeHistory}
            avgChangeByConfig={avgChangeByConfig}
            selectedConfig={selectedConfig}
            getAccelerationArrow={getAccelerationArrow}
            calculateStockScore={calculateStockScore}
            calculatePersonalizedAdjustment={calculatePersonalizedAdjustment}
            userProfile={userProfile}
            xAxisTickIntervalMinutes={5}
            aiAnthropicResult={aiAnthropicResult}
            aiOpenaiResult={aiOpenaiResult}
            marketSentiment={marketSentiment}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(stocksForDisplay.length / STOCKS_PER_PAGE)}
            onPageChange={changePage}
          />
        </div>

        {/* Resizer handle - only show when AI panel is open */}
        {isAiPanelOpen && (
          <div
            ref={resizeRef}
            onMouseDown={handleResizeStart}
            onDoubleClick={handleResizeDoubleClick}
            style={{
              width: '6px',
              cursor: 'col-resize',
              backgroundColor: isResizing ? '#3b82f6' : '#e5e7eb',
              transition: isResizing ? 'none' : 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
            onMouseEnter={(e) => { if (!isResizing) e.currentTarget.style.backgroundColor = '#d1d5db'; }}
            onMouseLeave={(e) => { if (!isResizing) e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
            title="Drag to resize, double-click to reset"
          >
            <div style={{
              width: '2px',
              height: '40px',
              backgroundColor: isResizing ? 'white' : '#9ca3af',
              borderRadius: '1px'
            }} />
          </div>
        )}

        {/* Right side: AI Analysis Panel */}
        <AiAnalysisPanel
          isOpen={isAiPanelOpen}
          onClose={() => setIsAiPanelOpen(false)}
          anthropicResult={aiAnthropicResult}
          openaiResult={aiOpenaiResult}
          isAnthropicLoading={isAnthropicLoading}
          isOpenaiLoading={isOpenaiLoading}
          onAnalyze={handleAiAnalysis}
          scanTime={aiScanTime}
          width={aiPanelWidth}
          stockCountOptions={AI_STOCK_COUNT_OPTIONS}
          stockCount={aiStockCount}
          onStockCountChange={setAiStockCount}
          riskAssessment={riskAssessment}
          isRiskLoading={isRiskLoading}
          onSaveScan={handleSaveScan}
          isScanSaved={isScanSaved}
          stockData={aiPayloadCache?.stocks}
          marketSentiment={marketSentiment}
          trendScore={trendScore}
          spreadClass={spreadClass}
          iwmSpyspread={iwmSpyspread}
        />

        {/* Broker Data Import Dialog */}
        {isBrokerImportOpen && (
          <BrokerDataImport
            onClose={() => setIsBrokerImportOpen(false)}
          />
        )}

        {/* Trading Journal Dialog */}
        {isJournalOpen && (
          <TradingJournal
            onClose={() => setIsJournalOpen(false)}
          />
        )}

        {/* Insights Dashboard Dialog */}
        {isInsightsOpen && (
          <InsightsDashboard
            onClose={() => setIsInsightsOpen(false)}
            onReviewNow={(dates) => {
              setIsInsightsOpen(false);
              setAiReviewInitialDates(dates);
              setIsAiReviewOpen(true);
            }}
          />
        )}

        {/* AI Review Dialog */}
        {isAiReviewOpen && (
          <AiReviewDialog
            onClose={() => { setIsAiReviewOpen(false); setAiReviewInitialDates(null); }}
            initialDates={aiReviewInitialDates}
          />
        )}

        {/* Profile Update Dialog */}
        {isProfileUpdateOpen && (
          <ProfileUpdateDialog
            onClose={() => setIsProfileUpdateOpen(false)}
            onOpenAiReview={(start, end) => {
              setAiReviewInitialDates({ start, end });
              setIsProfileUpdateOpen(false);
              setIsAiReviewOpen(true);
            }}
          />
        )}

        {/* Backtest Dialog */}
        {isBacktestOpen && (
          <BacktestDialog onClose={() => setIsBacktestOpen(false)} />
        )}
      </div>
    </div>
  );
};

export default React.memo(StockChartTable);
